import { getApiClientRuntime } from '../config';

/**
 * THE PRESIGNED-PHOTO TWO-HOP. Explained here, once, for every photo in the
 * product: a task photo, a worker's own avatar, a person's avatar in a roster.
 *
 * `GET /me/photo`, `GET /users/:id/photo` and `GET /tasks/:id/photos/:photoId`
 * answer a presigned GET on the object store. They carry no JSON envelope, so
 * they cannot go through `baseApi` / `zodResponse` like every other endpoint —
 * and a plain `<img src>` cannot reach them either, because the access token
 * lives in memory only (rule 14) and a browser never attaches an
 * `Authorization` header to an image request. Hence: fetch, blob, object URL.
 *
 * **Two hops, never one redirect.** In production the app and the bucket sit
 * behind one origin (Caddy), so a same-origin 302 KEEPS the `Authorization`
 * header, and MinIO answers **400 "multiple authentication types"** to a
 * presigned url that also carries a bearer. A worker's phone shows the same
 * thing over the LAN IP; a desktop dev box never does, because
 * localhost → LAN IP is cross-origin and drops the header on its own. So:
 *
 *   1. ask the API for the presigned url as JSON (`Accept: application/json`
 *      → `200 { url }` instead of the redirect), authorized with the bearer;
 *   2. fetch THAT url with **no auth header at all** — the object store is not
 *      the API, and carrying the bearer onto it is the bug.
 *
 * If you change one line in this file, it is that pair of hops you are
 * changing. `photoObjectUrl.spec.ts` asserts both headers.
 */

/** The most photos held as object URLs at once; the least recently read is revoked past it. */
export const PHOTO_CACHE_LIMIT = 50;

/**
 * Resolved (or in-flight) object URLs, keyed `${path}|${version}`.
 *
 * Module-level and deliberately outliving any component: many avatars of the
 * same person on screen at once (a roster row and a dialog) share one fetch,
 * and a `null` version answers with no fetch at all. Entries are dropped when
 * a *newer* version appears for the same path — the one signal that the old
 * blob is stale — or when the map passes {@link PHOTO_CACHE_LIMIT}, at which
 * point the least recently read entry is revoked. Without that cap, paging a
 * 200-person roster retained every blob for the life of the tab.
 *
 * A `Map` iterates in insertion order, which is what makes re-inserting on
 * every read a complete LRU in three lines.
 */
const cache = new Map<string, Promise<string>>();
/** The key last served for each path, so a newer version can revoke the old blob. */
const latestKeyByPath = new Map<string, string>();

function revoke(entry: Promise<string> | undefined): void {
  entry?.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);
}

function evictOverflow(): void {
  while (cache.size > PHOTO_CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (oldest.done === true) return;
    const entry = cache.get(oldest.value);
    cache.delete(oldest.value);
    revoke(entry);
  }
}

/** `${path}|${version}` — the identity of one photo at one moment. */
export function photoCacheKey(path: string, version: string | null): string {
  return `${path}|${version ?? ''}`;
}

/** How many photos are held right now. Exported for the spec and for diagnostics. */
export function photoCacheSize(): number {
  return cache.size;
}

/** Drop and revoke everything. Sessions do not share blobs; the spec uses it too. */
export function clearPhotoCache(): void {
  for (const entry of cache.values()) revoke(entry);
  cache.clear();
  latestKeyByPath.clear();
}

/**
 * The cached two-hop. `version` is whatever changes when the photo does —
 * `photoUpdatedAt` for an avatar, nothing at all for an immutable task photo.
 */
export function resolvePhotoObjectUrl(
  path: string,
  version: string | null,
  token: string | null,
): Promise<string> {
  const key = photoCacheKey(path, version);

  const previousKey = latestKeyByPath.get(path);
  latestKeyByPath.set(path, key);
  if (previousKey !== undefined && previousKey !== key) {
    const stale = cache.get(previousKey);
    cache.delete(previousKey);
    revoke(stale);
  }

  const cached = cache.get(key);
  if (cached !== undefined) {
    // Re-insert so the freshly read entry is the newest, never the next evicted.
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const entry = fetchPhotoObjectUrl(path, token);
  cache.set(key, entry);
  evictOverflow();

  entry.catch(() => {
    // A failure must not poison the cache: the next reader gets a fresh attempt.
    if (cache.get(key) === entry) cache.delete(key);
    if (latestKeyByPath.get(path) === key) latestKeyByPath.delete(path);
  });

  return entry;
}

async function fetchPhotoObjectUrl(path: string, token: string | null): Promise<string> {
  const runtime = getApiClientRuntime();
  const presign = await fetch(`${runtime.baseUrl}${path}?lang=${runtime.getLocale()}`, {
    headers: {
      Accept: 'application/json',
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
    },
  });
  if (!presign.ok) throw new Error(`photo presign fetch failed: ${String(presign.status)}`);

  const body: unknown = await presign.json();
  const url =
    typeof body === 'object' && body !== null && typeof (body as { url?: unknown }).url === 'string'
      ? (body as { url: string }).url
      : null;
  if (url === null) throw new Error('photo presign fetch: malformed body');

  // No Authorization here, on purpose — see the file doc comment.
  const object = await fetch(url);
  if (!object.ok) throw new Error(`photo object fetch failed: ${String(object.status)}`);

  return URL.createObjectURL(await object.blob());
}
